import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { TENANT_ORG_ID_STORAGE_KEY } from '../constants/tenant';
import { apiPath } from '../utils/apiBaseUrl';
import { tenantMatchesHost } from '../utils/tenantProfile';
import i18n from '../i18n';

const TENANT_BASE_DOMAIN = process.env.REACT_APP_TENANT_BASE_DOMAIN || '';

const TenantContext = createContext({
  tenant: null,
  loading: true,
  error: null,
  patchTenant: () => {},
});

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const host = window.location.hostname;
    try {
      localStorage.removeItem(TENANT_ORG_ID_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    axios
      .get(apiPath('/api/v1/organizations/resolve'), { params: { host }, timeout: 15_000 })
      .then((r) => {
        if (TENANT_BASE_DOMAIN && r.data?.slug && !tenantMatchesHost(r.data, host, TENANT_BASE_DOMAIN)) {
          throw new Error('tenant-host-mismatch');
        }
        setTenant(r.data);
        setError(null);
        if (r.data?.id) {
          try {
            localStorage.setItem(TENANT_ORG_ID_STORAGE_KEY, String(r.data.id));
          } catch {
            /* ignore quota / private mode */
          }
        }
        if (r.data?.name) {
          document.title = `${r.data.name} — Licenças`;
        }
      })
      .catch((e) => {
        setTenant(null);
        setError(e);
        try {
          localStorage.removeItem(TENANT_ORG_ID_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <p className="text-gray-600">{i18n.t('loading')}</p>
      </div>
    );
  }

  const publicWithoutTenant =
    typeof window !== 'undefined' &&
    /^\/(process-lookup|public-complaint|consulta-processo|denuncia)(\/|$)/.test(window.location.pathname);

  if (error && !tenant && !publicWithoutTenant) {
    const msg =
      error?.response?.status === 404
        ? i18n.t('tenantMissing', { ns: 'auth' })
        : i18n.t('tenantLoadError', { ns: 'auth' });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md text-center text-gray-700">
          <h1 className="text-xl font-semibold mb-2">{i18n.t('loadError')}</h1>
          <p>{msg}</p>
        </div>
      </div>
    );
  }

  const patchTenant = (partial) => {
    setTenant((current) => (current ? { ...current, ...partial } : current));
  };

  return (
    <TenantContext.Provider value={{ tenant, loading, error, patchTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
